'use client'

import * as React from 'react'
import { type JSONContent } from '@tiptap/core'
import { Split, AlignLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { type DiffOperation, type TiptapDiffResult } from '@/utils/tiptap-diff'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { trackEvent } from '@/lib/posthog'

// Character-level diff utilities for text content
function createCharacterDiff(oldText: string, newText: string) {
  // Simple character-level diff implementation
  const oldChars = oldText.split('')
  const newChars = newText.split('')

  const diff: Array<{
    type: 'equal' | 'insert' | 'delete'
    text: string
  }> = []

  let i = 0,
    j = 0

  while (i < oldChars.length || j < newChars.length) {
    if (
      i < oldChars.length &&
      j < newChars.length &&
      oldChars[i] === newChars[j]
    ) {
      // Equal character
      let equalText = ''
      while (
        i < oldChars.length &&
        j < newChars.length &&
        oldChars[i] === newChars[j]
      ) {
        equalText += oldChars[i]
        i++
        j++
      }
      if (equalText) {
        diff.push({ type: 'equal', text: equalText })
      }
    } else if (
      i < oldChars.length &&
      (j >= newChars.length || oldChars[i] !== newChars[j])
    ) {
      // Deleted character
      let deletedText = ''
      while (
        i < oldChars.length &&
        (j >= newChars.length || oldChars[i] !== newChars[j])
      ) {
        deletedText += oldChars[i]
        i++
        if (j < newChars.length && oldChars[i] === newChars[j]) break
      }
      if (deletedText) {
        diff.push({ type: 'delete', text: deletedText })
      }
    } else if (j < newChars.length) {
      // Inserted character
      let insertedText = ''
      while (
        j < newChars.length &&
        (i >= oldChars.length || oldChars[i] !== newChars[j])
      ) {
        insertedText += newChars[j]
        j++
        if (i < oldChars.length && oldChars[i] === newChars[j]) break
      }
      if (insertedText) {
        diff.push({ type: 'insert', text: insertedText })
      }
    }
  }

  return diff
}

interface EnhancedDiffNodeProps {
  node: JSONContent
  oldNode?: JSONContent
  diffType?: 'added' | 'deleted' | 'modified' | 'unchanged'
  showInlineDiff?: boolean
}

/**
 * Enhanced diff node that renders content similar to TipTap's snapshot comparison
 */
function EnhancedDiffNode({
  node,
  oldNode,
  diffType = 'unchanged',
  showInlineDiff = true,
}: EnhancedDiffNodeProps) {
  const renderTextWithDiff = (text: string, oldText?: string) => {
    if (!showInlineDiff || !oldText || oldText === text) {
      return (
        <span
          className={cn(
            diffType === 'added' && 'rounded-sm bg-[#E8F1E4] px-1 text-[#3F5937]',
            diffType === 'deleted' &&
              'rounded-sm bg-[#FCEAEA] px-1 text-[#9B3C3C] line-through',
            diffType === 'modified' && 'rounded-sm bg-[#F1EEE4] px-1 text-[#5C7050]'
          )}
        >
          {text}
        </span>
      )
    }

    // Create character-level diff
    const charDiff = createCharacterDiff(oldText, text)

    return (
      <span className='space-x-1'>
        {charDiff.map((chunk, index) => (
          <span
            key={index}
            className={cn(
              chunk.type === 'insert' &&
                'rounded-sm bg-[#D9EAD3] px-0.5 text-[#3F5937]',
              chunk.type === 'delete' &&
                'rounded-sm bg-[#F8DADA] px-0.5 text-[#9B3C3C] line-through',
              chunk.type === 'equal' && ''
            )}
          >
            {chunk.text}
          </span>
        ))}
      </span>
    )
  }

  const renderNodeContent = (): React.ReactNode => {
    switch (node.type) {
      case 'paragraph':
        return (
          <p
            className={cn(
              'prose prose-sm my-2 max-w-none leading-relaxed text-[#2D2D2D]',
              diffType === 'added' &&
                'border-l-2 border-[#5C7050] bg-[#F5F8F3] pl-4',
              diffType === 'deleted' &&
                'border-l-2 border-[#C96A6A] bg-[#FEF7F7] pl-4 opacity-70',
              diffType === 'modified' &&
                'border-l-2 border-[#8A9A7E] bg-[#FAFAF7] pl-4'
            )}
          >
            {node.content?.map((child, index) => (
              <EnhancedDiffNode
                key={index}
                node={child}
                oldNode={oldNode?.content?.[index]}
                diffType={diffType}
                showInlineDiff={showInlineDiff}
              />
            )) || (
              <span className='text-muted-foreground italic'>
                Empty paragraph
              </span>
            )}
          </p>
        )

      case 'text':
        const textContent = node.text || ''
        const oldTextContent = oldNode?.text || ''

        return renderTextWithDiff(textContent, oldTextContent)

      case 'heading':
        const level = node.attrs?.level || 1
        const HeadingComponent =
          level === 1
            ? 'h1'
            : level === 2
              ? 'h2'
              : level === 3
                ? 'h3'
                : level === 4
                  ? 'h4'
                  : level === 5
                    ? 'h5'
                    : 'h6'

        return React.createElement(
          HeadingComponent,
          {
            className: cn(
              'font-semibold leading-tight my-4 text-[#2D2D2D]',
              level === 1 && 'text-2xl',
              level === 2 && 'text-xl',
              level === 3 && 'text-lg',
              level >= 4 && 'text-base',
              diffType === 'added' &&
                'border-l-2 border-[#5C7050] pl-4 bg-[#F5F8F3]',
              diffType === 'deleted' &&
                'border-l-2 border-[#C96A6A] pl-4 bg-[#FEF7F7] opacity-70',
              diffType === 'modified' &&
                'border-l-2 border-[#8A9A7E] pl-4 bg-[#FAFAF7]'
            ),
          },
          node.content?.map((child, index) => (
            <EnhancedDiffNode
              key={index}
              node={child}
              oldNode={oldNode?.content?.[index]}
              diffType={diffType}
              showInlineDiff={showInlineDiff}
            />
          ))
        )

      case 'step':
        return (
          <div
            className={cn(
              'my-4 flex gap-4 rounded-lg border p-4 transition-colors',
              diffType === 'added' && 'border-green-200 bg-green-50/50',
              diffType === 'deleted' &&
                'border-red-200 bg-red-50/50 opacity-70',
              diffType === 'modified' && 'border-blue-200 bg-blue-50/50',
              diffType === 'unchanged' && 'border-border bg-background'
            )}
          >
            {/* Step number indicator */}
            {node.attrs?.number && (
              <div
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium',
                  diffType === 'added' &&
                    'border-2 border-green-300 bg-green-100 text-green-800',
                  diffType === 'deleted' &&
                    'border-2 border-red-300 bg-red-100 text-red-800',
                  diffType === 'modified' &&
                    'border-2 border-blue-300 bg-blue-100 text-blue-800',
                  diffType === 'unchanged' && 'bg-muted text-foreground'
                )}
              >
                {node.attrs.number}
              </div>
            )}

            <div className='min-w-0 flex-1'>
              {/* Step title */}
              {node.attrs?.title && (
                <h3 className='mb-2 text-base font-semibold'>
                  {renderTextWithDiff(node.attrs.title, oldNode?.attrs?.title)}
                </h3>
              )}

              {/* Step content */}
              <div className='space-y-2'>
                {node.content?.map((child, index) => (
                  <EnhancedDiffNode
                    key={index}
                    node={child}
                    oldNode={oldNode?.content?.[index]}
                    diffType={diffType}
                    showInlineDiff={showInlineDiff}
                  />
                ))}
              </div>
            </div>
          </div>
        )

      case 'card':
        return (
          <div
            className={cn(
              'my-4 rounded-lg border p-6 transition-colors',
              diffType === 'added' &&
                'border-green-200 bg-green-50/30 shadow-green-100',
              diffType === 'deleted' &&
                'border-red-200 bg-red-50/30 opacity-70 shadow-red-100',
              diffType === 'modified' &&
                'border-blue-200 bg-blue-50/30 shadow-blue-100',
              diffType === 'unchanged' &&
                'border-border bg-background shadow-sm'
            )}
          >
            {/* Card title */}
            {node.attrs?.title && (
              <h3 className='mb-4 text-lg font-semibold'>
                {renderTextWithDiff(node.attrs.title, oldNode?.attrs?.title)}
              </h3>
            )}

            {/* Card content */}
            <div className='space-y-3'>
              {node.content?.map((child, index) => (
                <EnhancedDiffNode
                  key={index}
                  node={child}
                  oldNode={oldNode?.content?.[index]}
                  diffType={diffType}
                  showInlineDiff={showInlineDiff}
                />
              ))}
            </div>
          </div>
        )

      case 'admonition':
        const admonitionType = node.attrs?.type || 'info'
        const typeStyles = {
          info: 'border-blue-200 bg-blue-50/50',
          warning: 'border-yellow-200 bg-yellow-50/50',
          error: 'border-red-200 bg-red-50/50',
          success: 'border-green-200 bg-green-50/50',
        }

        return (
          <div
            className={cn(
              'my-4 rounded-lg border p-4',
              typeStyles[admonitionType as keyof typeof typeStyles],
              diffType === 'added' && 'ring-2 ring-green-200',
              diffType === 'deleted' && 'opacity-70 ring-2 ring-red-200',
              diffType === 'modified' && 'ring-2 ring-blue-200'
            )}
          >
            {/* Admonition title */}
            {node.attrs?.title && (
              <h4 className='mb-2 font-medium'>
                {renderTextWithDiff(node.attrs.title, oldNode?.attrs?.title)}
              </h4>
            )}

            {/* Admonition content */}
            <div className='space-y-2'>
              {node.content?.map((child, index) => (
                <EnhancedDiffNode
                  key={index}
                  node={child}
                  oldNode={oldNode?.content?.[index]}
                  diffType={diffType}
                  showInlineDiff={showInlineDiff}
                />
              ))}
            </div>
          </div>
        )

      default:
        // Handle other node types with generic styling
        return (
          <div
            className={cn(
              'space-y-2 rounded-md border p-3',
              diffType === 'added' && 'border-green-200 bg-green-50/50',
              diffType === 'deleted' &&
                'border-red-200 bg-red-50/50 opacity-70',
              diffType === 'modified' && 'border-blue-200 bg-blue-50/50',
              diffType === 'unchanged' && 'border-border bg-muted/20'
            )}
          >
            <div className='text-muted-foreground text-sm'>
              {node.type} {node.attrs?.title && `- ${node.attrs.title}`}
            </div>
            {node.content && (
              <div className='mt-2 space-y-2'>
                {node.content.map((child, index) => (
                  <EnhancedDiffNode
                    key={index}
                    node={child}
                    oldNode={oldNode?.content?.[index]}
                    diffType={diffType}
                    showInlineDiff={showInlineDiff}
                  />
                ))}
              </div>
            )}
          </div>
        )
    }
  }

  return <>{renderNodeContent()}</>
}

/**
 * Enhanced diff renderer similar to TipTap's snapshot comparison
 */
export function EnhancedDiffRenderer({
  diffResult,
  previous,
  current,
  className,
}: {
  diffResult: TiptapDiffResult
  previous: JSONContent
  current: JSONContent
  className?: string
}) {
  const [viewMode, setViewMode] = React.useState<'unified' | 'split'>('unified')
  const [showInlineDiff, setShowInlineDiff] = React.useState(true)
  const [highlightChanges, setHighlightChanges] = React.useState(true)

  const trackDiffControl = React.useCallback(
    (action: string, properties?: Record<string, unknown>) => {
      trackEvent('doc.version_diff.control_clicked', {
        action,
        viewMode,
        added: diffResult.stats.added,
        modified: diffResult.stats.modified,
        deleted: diffResult.stats.deleted,
        ...properties,
      })
    },
    [
      diffResult.stats.added,
      diffResult.stats.deleted,
      diffResult.stats.modified,
      viewMode,
    ]
  )

  // Create mapping of operations by path for efficient lookup
  const operationMap = React.useMemo(() => {
    const map = new Map<string, DiffOperation>()
    diffResult.operations.forEach((op) => {
      map.set(op.path.join('.'), op)
    })
    return map
  }, [diffResult.operations])

  // Determine diff type for a node based on its path
  const getDiffType = (
    path: string[]
  ): 'added' | 'deleted' | 'modified' | 'unchanged' => {
    if (!highlightChanges) return 'unchanged'

    const pathKey = path.join('.')
    const operation = operationMap.get(pathKey)

    if (!operation) {
      // Check if parent path has operation
      for (let i = path.length - 1; i >= 0; i--) {
        const parentPath = path.slice(0, i).join('.')
        const parentOp = operationMap.get(parentPath)
        if (parentOp) {
          switch (parentOp.type) {
            case 'insert':
              return 'added'
            case 'delete':
              return 'deleted'
            case 'update':
              return 'modified'
          }
        }
      }
      return 'unchanged'
    }

    switch (operation.type) {
      case 'insert':
        return 'added'
      case 'delete':
        return 'deleted'
      case 'update':
        return 'modified'
      default:
        return 'unchanged'
    }
  }

  const renderDocument = (
    doc: JSONContent,
    oldDoc?: JSONContent,
    pathPrefix: string[] = []
  ) => {
    if (!doc.content) return null

    return doc.content.map((node, index) => {
      const nodePath = [...pathPrefix, 'content', index.toString()]
      const diffType = getDiffType(nodePath)
      const oldNode = oldDoc?.content?.[index]

      return (
        <EnhancedDiffNode
          key={`${pathPrefix.join('.')}-${index}`}
          node={node}
          oldNode={oldNode}
          diffType={diffType}
          showInlineDiff={showInlineDiff}
        />
      )
    })
  }

  const renderUnifiedView = () => (
    <div className='space-y-2'>{renderDocument(current, previous, [])}</div>
  )

  const renderSplitView = () => (
    <div className='grid grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)] gap-6'>
      {/* Previous version */}
      <div className='space-y-4 pt-6'>
        <div className='text-sm font-medium font-[Fraunces]'>
          Previous Version
        </div>
        <div className='space-y-1 opacity-75'>
          {renderDocument(previous, undefined, [])}
        </div>
      </div>

      <div className='w-px bg-[#E7E3E4]' aria-hidden='true' />

      {/* Current version with diff highlighting */}
      <div className='space-y-4 pt-6'>
        <div className='text-sm font-medium text-[#2D2D2D] font-[Fraunces]'>
          Current Version
        </div>
        <div className='space-y-1'>{renderDocument(current, previous, [])}</div>
      </div>
    </div>
  )

  return (
    <div className={cn('space-y-4', className)}>
      {/* Controls */}
      <div className='flex flex-wrap items-center gap-2 pb-1 text-sm'>
        <Button
          variant={viewMode === 'unified' ? 'default' : 'outline'}
          size='sm'
          onClick={() => {
            trackDiffControl('view_mode_changed', { nextViewMode: 'unified' })
            setViewMode('unified')
          }}
          className={cn(
            viewMode === 'unified' &&
              'bg-sidebar-primary text-white hover:bg-sidebar-primary/90'
          )}
        >
          <AlignLeft className='mr-1 h-3 w-3' />
          Unified
        </Button>

        <Button
          variant={viewMode === 'split' ? 'default' : 'outline'}
          size='sm'
          onClick={() => {
            trackDiffControl('view_mode_changed', { nextViewMode: 'split' })
            setViewMode('split')
          }}
          className={cn(
            viewMode === 'split' &&
              'bg-sidebar-primary text-white hover:bg-sidebar-primary/90'
          )}
        >
          <Split className='mr-1 h-3 w-3' />
          Split
        </Button>

        <div className='mx-1 h-5 w-px bg-[#E7E3E4]' />

        <label className='inline-flex h-8 items-center gap-2 text-xs font-medium text-[#6F6B67]'>
          <span>Inline changes</span>
          <Switch
            aria-label='Show inline changes'
            checked={showInlineDiff}
            onCheckedChange={(checked) => {
              trackDiffControl('inline_changes_toggled', { enabled: checked })
              setShowInlineDiff(checked)
            }}
            className='data-[state=checked]:bg-[#5C7050]'
          />
        </label>

        <label className='inline-flex h-8 items-center gap-2 text-xs font-medium text-[#6F6B67]'>
          <span>Highlight changes</span>
          <Switch
            aria-label='Highlight changes'
            checked={highlightChanges}
            onCheckedChange={(checked) => {
              trackDiffControl('highlight_changes_toggled', {
                enabled: checked,
              })
              setHighlightChanges(checked)
            }}
            className='data-[state=checked]:bg-[#5C7050]'
          />
        </label>

        <div className='ml-auto text-xs text-[#8E8A85]'>
          {diffResult.stats.added} added, {diffResult.stats.modified} modified,{' '}
          {diffResult.stats.deleted} deleted
        </div>
      </div>

      {/* Diff 正文单独放在白色内容卡片中，工具栏保持在卡片外部 */}
      <div className='min-h-[200px] rounded-lg border border-[#E7E3E4] bg-white p-6 pt-0'>
        {viewMode === 'split' ? renderSplitView() : renderUnifiedView()}
      </div>

      {/* Empty state */}
      {diffResult.operations.length === 0 && (
        <div className='text-muted-foreground py-12 text-center'>
          <div className='mb-2 text-lg font-medium'>No changes detected</div>
          <div className='text-sm'>The documents are identical</div>
        </div>
      )}
    </div>
  )
}
